/**
 * DV-FF-002: Feature Flags API Client
 *
 * Client for feature flags CRUD operations with admin controls and audit logging.
 * Updated for DV-INF-002: Uses HTTP endpoints with proper headers and API-first policy.
 */

import { buildApiUrl, API_ENDPOINTS, getApiHeadersGet, getApiHeadersJson } from '@/lib/api-base';
import { getDemoError } from '@/lib/demo-error-handler';
import type {
  FeatureFlag,
  FeatureFlagStatus,
  FeatureFlagAuditEntry,
  FeatureFlagUpdate,
  FeatureFlagsAPI
} from '@/types/feature-flags';

// DV-FF-002: Use HTTP endpoints with proper headers instead of Supabase client

/**
 * Feature Flags API Client Implementation
 * DV-FF-002: Uses HTTP endpoints with proper headers and error handling
 */
class FeatureFlagsAPIClient implements FeatureFlagsAPI {
  private baseUrl = API_ENDPOINTS.demo.featureFlags;

  // Track recent API calls for status reports
  private recentApiCalls: Array<{
    method: string;
    path: string;
    status: number;
    elapsed: number;
    requestId?: string;
    timestamp: string;
  }> = [];

  private logApiCall(method: string, path: string, status: number, elapsed: number, requestId?: string) {
    this.recentApiCalls.unshift({
      method,
      path: path.replace(API_ENDPOINTS.demo.featureFlags, '/api/demo/feature-flags'),
      status,
      elapsed,
      requestId,
      timestamp: new Date().toISOString()
    });

    // Keep only last 10 calls
    if (this.recentApiCalls.length > 10) {
      this.recentApiCalls = this.recentApiCalls.slice(0, 10);
    }
  }

  getRecentApiCalls() {
    return [...this.recentApiCalls];
  }

  // List all feature flags
  async listFlags(): Promise<FeatureFlag[]> {
    const url = buildApiUrl(this.baseUrl);
    const startTime = performance.now();

    const response = await fetch(url, {
      credentials: 'include',
      headers: getApiHeadersGet(), // No Content-Type for GET requests
      cache: 'no-store', // API-first policy
    });

    const elapsed = Math.round(performance.now() - startTime);
    const requestId = response.headers.get('X-Request-Id') || undefined;

    this.logApiCall('GET', this.baseUrl, response.status, elapsed, requestId);

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`
      }));

      const demoError = getDemoError({
        message: error.message || 'Failed to fetch feature flags',
        status: response.status,
        code: error.code,
        details: error.details
      });

      throw demoError;
    }

    const data = await response.json();
    return data.flags || data || [];
  }

  // Check if current user is admin
  async isCurrentUserAdmin(): Promise<boolean> {
    try {
      const adminStatus = await this.checkAdminAccess();
      return adminStatus;
    } catch {
      return false;
    }
  }

  // Get effective status for a specific flag and brand
  async getFlagStatus(name: string, brandId: string = 'hi-si'): Promise<FeatureFlagStatus> {
    const params = new URLSearchParams({
      name,
      brand_id: brandId,
    });
    const url = buildApiUrl(`${this.baseUrl}/status?${params}`);

    const response = await fetch(url, {
      credentials: 'include',
      headers: getApiHeadersGet(),
      cache: 'no-store',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`
      }));

      const demoError = getDemoError({
        message: error.message || `Failed to get flag status for ${name}`,
        status: response.status,
        code: error.code,
        details: error.details
      });

      throw demoError;
    }

    return response.json();
  }

  // Get audit log entries
  async getAuditLog(flagName?: string, limit: number = 50): Promise<FeatureFlagAuditEntry[]> {
    const params = new URLSearchParams({
      limit: limit.toString(),
    });

    if (flagName) {
      params.append('flag_name', flagName);
    }

    const url = buildApiUrl(`${this.baseUrl}/audit?${params}`);

    const response = await fetch(url, {
      credentials: 'include',
      headers: getApiHeadersGet(),
      cache: 'no-store',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`
      }));

      const demoError = getDemoError({
        message: error.message || 'Failed to fetch audit log',
        status: response.status,
        code: error.code,
        details: error.details
      });

      throw demoError;
    }

    const data = await response.json();
    return data.entries || data || [];
  }

  // Update feature flag (admin only)
  async updateFlag(update: FeatureFlagUpdate): Promise<FeatureFlag> {
    const url = buildApiUrl(`${this.baseUrl}/${update.name}`);
    const startTime = performance.now();

    const response = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: getApiHeadersJson(), // Include Content-Type for PATCH requests
      body: JSON.stringify(update),
    });

    const elapsed = Math.round(performance.now() - startTime);
    const requestId = response.headers.get('X-Request-Id') || undefined;

    this.logApiCall('PATCH', `${this.baseUrl}/${update.name}`, response.status, elapsed, requestId);

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`
      }));

      const demoError = getDemoError({
        message: error.message || `Failed to update feature flag: ${update.name}`,
        status: response.status,
        code: error.code,
        details: error.details
      });

      throw demoError;
    }

    return response.json();
  }

  // Create new feature flag (admin only)
  async createFlag(flag: Omit<FeatureFlag, 'id' | 'created_at' | 'updated_at'>, reason?: string): Promise<FeatureFlag> {
    const url = buildApiUrl(this.baseUrl);

    const requestBody = {
      ...flag,
      reason
    };

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: getApiHeadersJson(),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`
      }));

      const demoError = getDemoError({
        message: error.message || `Failed to create feature flag: ${flag.name}`,
        status: response.status,
        code: error.code,
        details: error.details
      });

      throw demoError;
    }

    return response.json();
  }

  // Delete feature flag (admin only)
  async deleteFlag(name: string, reason?: string): Promise<void> {
    const url = buildApiUrl(`${this.baseUrl}/${name}`);

    const requestBody = reason ? { reason } : {};

    const response = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: getApiHeadersJson(),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`
      }));

      const demoError = getDemoError({
        message: error.message || `Failed to delete feature flag: ${name}`,
        status: response.status,
        code: error.code,
        details: error.details
      });

      throw demoError;
    }
  }

  // Batch get multiple flag statuses
  async getFlagStatuses(names: string[], brandId: string = 'hi-si'): Promise<Record<string, FeatureFlagStatus>> {
    const results: Record<string, FeatureFlagStatus> = {};

    // Get all flags in parallel
    const promises = names.map(async (name) => {
      try {
        const status = await this.getFlagStatus(name, brandId);
        results[name] = status;
      } catch (error) {
        // Handle missing flags gracefully
        results[name] = {
          enabled: false,
          reason: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          flag_data: null as any
        };
      }
    });

    await Promise.all(promises);
    return results;
  }

  // Check if current user has admin access
  async checkAdminAccess(): Promise<boolean> {
    const url = buildApiUrl(`${this.baseUrl}/admin-check`);

    const response = await fetch(url, {
      credentials: 'include',
      headers: getApiHeadersGet(),
      cache: 'no-store',
    });

    if (!response.ok) {
      // Non-admin users will get 403, which is expected
      if (response.status === 403) {
        return false;
      }

      const error = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`
      }));

      const demoError = getDemoError({
        message: error.message || 'Failed to check admin access',
        status: response.status,
        code: error.code,
        details: error.details
      });

      throw demoError;
    }

    const data = await response.json();
    return data.isAdmin || data.admin || false;
  }
}

// Export singleton instance
export const featureFlagsAPI = new FeatureFlagsAPIClient();
export default featureFlagsAPI;

// Export class for testing
export { FeatureFlagsAPIClient };